import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getCurrentUser } from '../utils/auth';
import { getBaseUrl } from '../utils/getBaseUrl';
import apiClient from '../utils/apiClient';

const SocketContext = createContext(null);

/**
 * Derives the Socket.IO server URL from the API base URL.
 * API base: http://localhost:5300/api → Socket server: http://localhost:5300
 */
function getSocketUrl() {
  const base = getBaseUrl(); // e.g. "http://localhost:5300/api"
  return base.replace(/\/api\/?$/, '');
}

/**
 * Convert a base64 VAPID key to Uint8Array for PushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function SocketProvider({ children }) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [user, setUser] = useState(() => getCurrentUser());
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadWhatsAppCount, setUnreadWhatsAppCount] = useState(0);
  // Track which batchId the user currently has open in the chat page
  const activeChatBatchRef = useRef(null);

  // ── Listen for auth changes (login/logout) so we reconnect the socket ──
  useEffect(() => {
    const onAuthChange = () => setUser(getCurrentUser());
    window.addEventListener('vc-auth-change', onAuthChange);

    // Cross-tab sync: detect login/logout in other tabs via localStorage events
    const onStorage = (e) => {
      if (e.key === 'token' || e.key === 'user') {
        setUser(getCurrentUser());
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('vc-auth-change', onAuthChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ── Connect Socket ──
  useEffect(() => {
    if (!user) return;

    const socketUrl = getSocketUrl();
    const token = localStorage.getItem('token');
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      auth: { token },
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      console.log('[Socket] Connected:', socket.id);

      // Register with the server so we join the right rooms
      socket.emit('register', {
        userId: user._id || user.id,
        usertype: user.usertype,
        studentId: user.studentId,
        batchId: user.batchId,
      });
    });

    socket.on('disconnect', () => {
      setConnected(false);
      console.log('[Socket] Disconnected');
    });

    // ── Force-logout: another session kicked this one out ──
    socket.on('force-logout', ({ reason, previousSessionToken } = {}) => {
      // Verify this event is actually meant for THIS session — compare the
      // previousSessionToken from the server with our current JWT's
      // sessionToken. If they don't match, this event is stale (e.g. fired
      // by our own re-login from the same browser) and should be ignored.
      try {
        const currentToken = localStorage.getItem('token');
        if (currentToken && previousSessionToken) {
          const payload = JSON.parse(atob(currentToken.split('.')[1]));
          if (payload?.sessionToken && payload.sessionToken !== previousSessionToken) {
            console.log('[Socket] Ignoring stale force-logout (sessionToken mismatch)');
            return;
          }
        }
      } catch (_) { /* fall through to logout */ }

      console.warn('[Socket] Force-logout received:', reason);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedRole');
      window.dispatchEvent(new Event('vc-auth-change'));
      // Store reason so login page can show it
      sessionStorage.setItem('logoutReason', reason || 'You have been logged out because you logged in from another device or tab.');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    });

    // ── Listen for notifications ──
    socket.on('notification', (data) => {
      console.log('[Socket] Notification received:', data);
      setNotifications(prev => [{ ...data, _id: Date.now().toString(), read: false, createdAt: data.createdAt || new Date().toISOString() }, ...prev]);
      setUnreadCount(prev => prev + 1);
    });

    // ── Track unread chat messages for sidebar badge ──
    socket.on('chat:notification', (data) => {
      const currentUserId = user?._id || user?.id;
      if (data.sender === currentUserId) return;
      // Don't count if user is actively viewing the chat page
      if (window.location.pathname.includes('/chat')) return;
      setUnreadChatCount(prev => prev + 1);
    });

    // ── Track unread WhatsApp messages for sidebar badge ──
    socket.on('whatsapp:newMessage', (data) => {
      if (data.direction !== 'inbound') return;
      // Don't increment badge if user is currently viewing WhatsApp inbox
      const path = window.location.pathname;
      const hash = window.location.hash;
      const isOnWhatsApp =
        (path.includes('/whatsapp') && hash === '#inbox') ||
        path.includes('/whatsapp-inbox');
      if (isOnWhatsApp) {
        // Play a subtle sound instead of showing badge/notification
        try { new Audio('/sounds/wa-notify.mp3').play().catch(() => {}); } catch {}
        return;
      }
      setUnreadWhatsAppCount(prev => prev + 1);
    });

    // ── Generic in-app notification (fired by backend for events that should
    //    surface as a global toast — e.g. WhatsApp inbound, lead/demo alerts).
    //    Skips duplication when the user is already on the relevant page.
    socket.on('notification:new', (data = {}) => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      const type = data.type || 'general';

      const isOnWhatsApp =
        (path.includes('/whatsapp') && hash === '#inbox') ||
        path.includes('/whatsapp-inbox');

      // Suppress full toast when user is already on the WhatsApp page —
      // play subtle sound only.
      if (type === 'whatsapp_message' && isOnWhatsApp) {
        try { new Audio('/sounds/wa-notify.mp3').play().catch(() => {}); } catch {}
        return;
      }

      // Play sound for any push-style notification
      try {
        const src = type === 'whatsapp_message' ? '/sounds/wa-notify.mp3' : '/sounds/wa-notify.mp3';
        new Audio(src).play().catch(() => {});
      } catch {}

      // Hand off to InAppNotification (toast/banner) via window event
      try {
        window.dispatchEvent(new CustomEvent('vc-notification', {
          detail: {
            type,
            title: data.title || 'Notification',
            message: data.body || '',
            data: { url: data.url, ...(data.meta || {}) },
            link: data.url,
            tag: `${type}-${data.timestamp || Date.now()}`,
          },
        }));
      } catch {}

      // Also append to the bell-dropdown notifications list
      setNotifications(prev => [{
        _id: `live-${Date.now()}`,
        title: data.title || 'Notification',
        body: data.body || '',
        type,
        url: data.url,
        meta: data.meta || {},
        read: false,
        createdAt: data.timestamp || new Date().toISOString(),
      }, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user]); // reconnect whenever user changes (login/logout)

  // ── Fetch initial notifications from API ──
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    const fetchNotifications = async () => {
      try {
        const { data } = await apiClient.get('/notifications?pageSize=20');
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      } catch (err) {
        console.error('[Notifications] Failed to fetch:', err);
      }
    };
    fetchNotifications();
  }, [user]); // refetch when user changes

  // ── Fetch initial WhatsApp unread count ──
  useEffect(() => {
    if (!user) {
      setUnreadWhatsAppCount(0);
      return;
    }
    // Only fetch for admin/superadmin (institute) or tenantadmin (platform)
    const ut = user.usertype;
    if (!['admin', 'superadmin', 'tenantadmin'].includes(ut)) return;

    const endpoint = ut === 'tenantadmin'
      ? '/platform/whatsapp/unread-count'
      : '/whatsapp/unread-count';

    apiClient.get(endpoint)
      .then(({ data }) => {
        setUnreadWhatsAppCount(data.unreadCount || 0);
      })
      .catch(() => {});
  }, [user]);

  // ── Register Service Worker & subscribe to browser push ──
  useEffect(() => {
    if (!user) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[Push] Browser does not support push notifications');
      return;
    }

    const registerPush = async () => {
      try {
        // 1. Get the active service worker registration
        //    (registered in index.js for production, or from public/sw.js in dev)
        let registration = await navigator.serviceWorker.getRegistration('/');
        if (!registration) {
          registration = await navigator.serviceWorker.register('/sw.js');
        }
        console.log('[Push] Service Worker ready');

        // 2. Get the VAPID public key from the server
        const { data } = await apiClient.get('/notifications/vapid-public-key');
        const vapidKey = urlBase64ToUint8Array(data.publicKey);

        // 3. Check for existing subscription or create one
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          // Ask user for notification permission and subscribe
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            console.log('[Push] Notification permission denied');
            return;
          }

          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKey,
          });
          console.log('[Push] New push subscription created');
        }

        // 4. Send subscription to backend
        await apiClient.post('/notifications/push-subscribe', {
          subscription: subscription.toJSON(),
        });
        setPushEnabled(true);
        console.log('[Push] Subscription sent to server');
      } catch (err) {
        console.error('[Push] Failed to register push:', err);
      }
    };

    registerPush();
  }, [user]); // re-register push when user changes

  // ── Mark single notification as read ──
  const markAsRead = useCallback(async (notifId) => {
    try {
      await apiClient.post(`/notifications/${notifId}/read`);
      setNotifications(prev =>
        prev.map(n => n._id === notifId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[Notifications] Failed to mark as read:', err);
    }
  }, []);

  // ── Mark all as read ──
  const markAllAsRead = useCallback(async () => {
    try {
      await apiClient.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('[Notifications] Failed to mark all as read:', err);
    }
  }, []);

  // ── Clear unread chat count (call when user opens chat page) ──
  const clearUnreadChat = useCallback(() => {
    setUnreadChatCount(0);
  }, []);

  // ── Clear unread WhatsApp count (call when user opens WhatsApp inbox) ──
  const clearUnreadWhatsApp = useCallback(() => {
    setUnreadWhatsAppCount(0);
  }, []);

  const value = {
    socket: socketRef.current,
    connected,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    pushEnabled,
    unreadChatCount,
    clearUnreadChat,
    unreadWhatsAppCount,
    clearUnreadWhatsApp,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

export default SocketContext;
